import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import { APIService, KPI } from '../api';
import { spendingCategoriesSection } from '../api/models';

@Injectable()
export class InvestmentCategoriesService {

  public toogleDisplayMode$ = new BehaviorSubject<boolean>(null);

  private _investmentCategoriesOrdered$: Observable<KPI.InvestmentCategory[]>;
  private investmentCategoriesOrderedRefresh$ = new BehaviorSubject<void>(null);

  private _investmentCategories$: Observable<KPI.InvestmentCategory[]>;
  private investmentCategoriesRefresh$ = new BehaviorSubject<void>(null);

  constructor(private api: APIService) {
  }

  public get investmentCategoriesOrdered$(): Observable<KPI.InvestmentCategory[]> {
    if (!this._investmentCategoriesOrdered$) {
      this._investmentCategoriesOrdered$ = this.investmentCategoriesOrderedRefresh$.pipe(
        switchMap(() => this.api.investments_investment_category_ordered()),
        shareReplay(1),
      );
    }

    return this._investmentCategoriesOrdered$;
  }

  public refreshInvestmentCategoriesOrdered(): void {
    this.investmentCategoriesOrderedRefresh$.next();
  }

  public get investmentCategories$(): Observable<KPI.InvestmentCategory[]> {
    if (!this._investmentCategories$) {
      this._investmentCategories$ = this.investmentCategoriesRefresh$.pipe(
        switchMap(() => this.api.investments_investment_category()),
        shareReplay(1),
      );
    }

    return this._investmentCategories$;
  }

  public refreshInvestmentCategories(): void {
    this.investmentCategoriesRefresh$.next();
  }

    /**
   * Initialise the sections for investment categories (AKA "spending categories",
   * AKA "catégories de dépense" AKA "Nomenclature des achats") and look for suggestions
   * based on the technical nomenclature of the investment or its asset.
   * @param investment
   * @param asset
   * @returns
   */
  public initInvestmentCategories(
    investment: KPI.MappedInvestment,
    asset?: KPI.MappedAsset
  ): Observable<{
    sections: spendingCategoriesSection[];
    suggestedSpendingCategories: KPI.InvestmentCategory[];
  }> {
    return this.investmentCategories$.pipe(
      map(allCategories => {
        const parents = allCategories.filter(cat => !cat.parent_id);
        let sections: spendingCategoriesSection[] = [];
        sections.push({
          enabled: true,
          selectedCategory: null,
          selectedCategoryId: null,
          categories: parents,
        });

        let i = 0;
        while (this.hasSubCategory(sections[i])) {
          // We don't need to fill the other sections right now, because it should be filled
          // with the children of the category choosen by the user. But we need to know how
          // many levels do we have. So we have no choice than to fill the next sections and see
          // if they have more children
          sections[i + 1] = {
            enabled: false,
            selectedCategory: null,
            selectedCategoryId: null,
            categories: [],
          };
          sections[i].categories.forEach(category => {
            if (category.children.length) {
              sections[i + 1].categories = sections[i + 1].categories.concat(category.children);
            }
          });
          i++;
        }

        let suggestedSpendingCategories = [];
        if (asset || investment.category || investment.super_category) {
          // If we have a technical nomenclature, we look for matches
          const lastLevelCategories = sections[sections.length - 1].categories;
          suggestedSpendingCategories = lastLevelCategories.filter(category =>
            this.doesCategoryMatchNomenclature(category, allCategories, investment)
          );
        }
        return {
          sections: sections,
          suggestedSpendingCategories: suggestedSpendingCategories,
        };
      })
    );
  }

  private hasSubCategory(section: spendingCategoriesSection): boolean {
    return !!section.categories.find(category => category.children.length > 0);
  }

  /**
   * Check if an investment category or its parents matches the technical nomenclature
   * @param categoryToCheck
   * @param allCategories
   * @returns
   */
  private doesCategoryMatchNomenclature(
    categoryToCheck: KPI.InvestmentCategory,
    allCategories: KPI.InvestmentCategory[],
    investment: KPI.MappedInvestment
  ): boolean {
    const technicalCategoryId = investment.category // || asset?.category.id;
    const technicalSubCategoryId = investment.sub_category  // || asset?.subCategory.id;

    let cat: KPI.InvestmentCategory = categoryToCheck;
    let matchFound = false;
    while (!matchFound && cat != null) {
      if (
        cat.categories?.includes(technicalCategoryId) ||
        cat.subcategories?.includes(technicalSubCategoryId)
      ) {
        matchFound = true;
      }
      // If we haven't found a match, we check the parent category
      if (cat.parent_id) {
        const nextLevelCat = allCategories.find(category => category.id === cat.parent_id);
        cat = nextLevelCat;
      } else {
        cat = null;
      }
    }
    return matchFound;
  }

  public toggleDisplay() {
    this.toogleDisplayMode$.next(true);
  }
}
